import type { PSScopingContextValue } from "./PSScopingContext";

/**
 * Reading a PowerSchool connector's project-level filters out of untyped `vars`. Lives
 * outside `PowerSchoolsForm` so `ConnectorVarsForm` can provide `PSScopingContext` above
 * every child rather than inside one of them (SIM-3402).
 */

/**
 * `vars.filters` shape mirroring `powerschoolFiltersSchema` in `packages/core/src/schemas.ts`.
 * Kept local to avoid coupling the form to the zod-inferred types — vars arrive as
 * `Record<string, unknown>`.
 */
export interface ProjectFilters {
  schoolids?: string[];
  sinceDate?: string;
  schoolYearStart?: string;
  // SIM-2274 opt-in: seed the student pool from reenrollments (captures former
  // students who transferred to other district schools).
  studentPoolFromReenrollments?: boolean;
}

function readStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((x): x is string => typeof x === "string") : undefined;
}

export function readProjectFilters(vars: Record<string, unknown>): ProjectFilters {
  const f: unknown = vars.filters;
  if (!f || typeof f !== "object" || Array.isArray(f)) return {};
  const obj = f as Record<string, unknown>;
  const out: ProjectFilters = {};
  const schoolids = readStringArray(obj.schoolids);
  if (schoolids) out.schoolids = schoolids;
  if (typeof obj.sinceDate === "string") out.sinceDate = obj.sinceDate;
  if (typeof obj.schoolYearStart === "string") out.schoolYearStart = obj.schoolYearStart;
  if (typeof obj.studentPoolFromReenrollments === "boolean")
    out.studentPoolFromReenrollments = obj.studentPoolFromReenrollments;
  return out;
}

export function readEffectiveSchoolIds(vars: Record<string, unknown>): string[] {
  const filters = readProjectFilters(vars);
  if (filters.schoolids && filters.schoolids.length > 0) return filters.schoolids;
  const legacy = typeof vars.schoolid === "string" ? vars.schoolid.trim() : "";
  return legacy ? [legacy] : [];
}

/**
 * The scoping a per-(table, schedule) editor inherits from, or null when the connector has
 * no such concept. Null for the nine non-PowerSchool types, which is what the context's
 * consumers already fall back on.
 */
export function readPSScoping(
  vars: Record<string, unknown>,
  connectorType: string,
): PSScopingContextValue | null {
  if (connectorType !== "powerschools") return null;
  const filters = readProjectFilters(vars);
  return {
    projectSchoolIds: readEffectiveSchoolIds(vars),
    projectSinceDate: filters.sinceDate ?? "",
    // An empty string means unset here, so it must not reach consumers as a value.
    schoolYearStart: filters.schoolYearStart === "" ? undefined : filters.schoolYearStart,
  };
}
